import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppChat from "@/components/WhatsAppChat";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { 
  Target, 
  Users, 
  Zap, 
  Award,
  CheckCircle2,
  ArrowRight
} from "lucide-react";

export default function AboutPage() {
  const navigate = useNavigate();

  const stats = [
    { value: "75+", label: "Marketing Tools" },
    { value: "10K+", label: "Active Users" },
    { value: "50+", label: "Countries" },
    { value: "24/7", label: "Support" }
  ];

  const values = [
    {
      icon: <Target className="w-8 h-8 text-violet-600" />,
      title: "Results-Focused",
      description: "We build tools that deliver measurable results for your digital marketing campaigns."
    },
    {
      icon: <Users className="w-8 h-8 text-violet-600" />,
      title: "User-Centric",
      description: "Every feature is designed with our users in mind, making complex tasks simple."
    },
    {
      icon: <Zap className="w-8 h-8 text-violet-600" />,
      title: "Innovation",
      description: "We continuously evolve our tools with the latest AI and marketing technologies."
    },
    {
      icon: <Award className="w-8 h-8 text-violet-600" />,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from code to customer support."
    }
  ];

  const features = [
    "SEO Dashboard & Technical SEO Tools (21+)",
    "AI-Powered Content Writer & Rewriter",
    "Comprehensive PDF Tools Suite (32+)",
    "Social Media Management",
    "Google Ads & Facebook Ads Tools",
    "Team Collaboration & Permissions",
    "White-Label Reports",
    "Multi-Language Support (English & Hindi)"
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-700 to-purple-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About MarkeeMix
            </h1>
            <p className="text-xl text-violet-100 max-w-3xl mx-auto mb-8">
              We're on a mission to democratize digital marketing tools, making powerful SEO and marketing capabilities accessible to everyone.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur rounded-lg p-4">
                  <div className="text-3xl font-bold">{stat.value}</div>
                  <div className="text-violet-200 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  MarkeeMix was born from a simple observation: powerful marketing tools shouldn't be reserved only for big corporations with deep pockets.
                </p>
                <p>
                  Founded by CodewithKamlesh, we set out to build a comprehensive digital marketing suite that combines the power of AI with intuitive design, making professional-grade tools accessible to businesses of all sizes.
                </p>
                <p>
                  Today, MarkeeMix offers 75+ tools across SEO, content marketing, social media, advertising, and design - all in one integrated platform.
                </p>
              </div>
              <Button 
                className="mt-6 bg-violet-600 hover:bg-violet-700"
                onClick={() => navigate("/contact")}
              >
                Get in Touch <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl p-8">
              <h3 className="font-semibold text-lg mb-4">What We Offer</h3>
              <ul className="space-y-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, idx) => (
                <Card key={idx} className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {value.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-700 to-purple-600 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Marketing?
            </h2>
            <p className="text-violet-100 mb-8">
              Join thousands of marketers who trust MarkeeMix for their digital success.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                className="bg-white text-violet-700 hover:bg-gray-100"
                onClick={() => navigate("/tools")}
              >
                Explore Tools
              </Button>
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate("/pricing")}
              >
                View Pricing
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppChat />
    </div>
  );
}
